import { getReferenceString, MedplumClient, ProfileResource } from '@medplum/core';
import {
  AccessPolicy,
  ContactPoint,
  Practitioner,
  PractitionerRole,
  Project,
  ProjectMembership,
  User,
  UserConfiguration,
} from '@medplum/fhirtypes';
import { PROVIDER_ROLE_CODING_MAP } from './codings';
import {
  BENEFIT_EVENT_WRITE_ROLES,
  INSURANCE_CARD_EDIT_ROLES,
  CLINICAL_RECORD_HIDDEN_ROLES,
  CONVERSATIONS_HIDDEN_ROLES,
  ON_CALL_SCHEDULE_EDIT_ROLES,
  PAYMENTS_ROLES,
  PAYMENTS_VIEW_ROLES,
  INTAKE_HUB_ROLES,
  COVERAGE_HUB_ROLES,
  PRIMARY_ROLES,
  ProviderRole,
  SERVICE_ACCOUNT_IDENTIFIER_SYSTEM,
} from './constants';
import { PAC_MANAGER_ACCESS_EXTENSION, CUSTOM_PERMISSIONS_EXTENSION, IVR_CONFIG_ACCESS_EXTENSION } from './extensions';

export type PractitionerWithRole = Practitioner & { roleLabel?: string; roleCode?: ProviderRole };

export function isProvider(profile: ProfileResource | undefined): profile is Practitioner {
  return !!profile && profile.resourceType === 'Practitioner';
}

/**
 * Whether a Practitioner is an automated sender rather than a person (BAR-2756, BAR-2864).
 *
 * Identified by the service-account identifier system rather than by name or id, so a new
 * automated sender is recognised the day it is created. Typed structurally: a GraphQL selection
 * that asked for `identifier { system }` passes as well as a full resource.
 */
export function isServiceAccount(profile: { identifier?: Array<{ system?: string }> } | undefined): boolean {
  return !!profile?.identifier?.some((identifier) => identifier.system === SERVICE_ACCOUNT_IDENTIFIER_SYSTEM);
}

export function getPrimaryProviderRole(practitionerRole: PractitionerRole | undefined): ProviderRole | undefined {
  if (!practitionerRole) return undefined;

  return practitionerRole.code?.find((code) => PRIMARY_ROLES.includes(code.coding?.[0]?.code as ProviderRole))
    ?.coding?.[0]?.code as ProviderRole;
}

export function isAdmin(practitionerRole: PractitionerRole | undefined): boolean {
  if (!practitionerRole) return false;

  return practitionerRole.code?.some((code) => code.coding?.[0]?.code === ProviderRole.ADMINISTRATOR) ?? false;
}

/**
 * Whether the user may configure the IVR (business hours, menu templates, on-call NP
 * schedule). True for administrators, who hold AdminAccessPolicy, and for anyone
 * granted the opt-in permission, who is given IvrConfigAccessPolicy by
 * practitionerrole-subscription. Both policies cover the Basic and Schedule resources
 * the IVR pages read and write, so this predicate matches what the server allows.
 */
export function canConfigureIvr(practitionerRole: PractitionerRole | undefined): boolean {
  if (!practitionerRole) return false;

  return isAdmin(practitionerRole) || hasCustomPermission(practitionerRole, IVR_CONFIG_ACCESS_EXTENSION);
}

/**
 * Whether the user may view the Patient Access manager dashboard (BAR-2513).
 *
 * Deliberately NOT gated on the PATIENT_ACCESS role: a PAC manager holds that role
 * themselves, so a role check would show every coordinator the report on their
 * own and their colleagues' performance. The opt-in permission is granted per
 * practitioner, the way IVR config is.
 *
 * This gates the nav entry and route only. What the dashboard can actually read
 * is decided by the access policy on the server, which is verified separately
 * (BAR-2520) — `MockClient` does not enforce policies, so a green test here
 * proves nothing about the data being readable.
 */
export function canViewCiManagerDashboard(practitionerRole: PractitionerRole | undefined): boolean {
  if (!practitionerRole) return false;

  return isAdmin(practitionerRole) || hasCustomPermission(practitionerRole, PAC_MANAGER_ACCESS_EXTENSION);
}

/**
 * Whether the user may create a treatment payment order and send its payment
 * link (BAR-2406, M5).
 *
 * A single predicate rather than a role list, because it is consumed from two
 * places that must not diverge: the `create-treatment-payment` bot, where it is
 * the actual authorisation boundary, and `canAccessPayments` on
 * `CurrentUserProvider`, which decides whether the screen appears at all. A
 * shared list would still have let the two disagree about admins — as they did.
 *
 * `isAdmin` is included to match every other permission in this file and in
 * `CurrentUserProvider`. It is not much of a boundary in any case: an
 * administrator holds `AdminAccessPolicy` and can add `PATIENT_ACCESS` to their own
 * `PractitionerRole`, so excluding them would only make the honest path awkward.
 *
 * `ProjectMembership.admin` is deliberately NOT part of this, for the reason
 * given on `canConfigureIvr`'s caller: project administration is not the same
 * grant as permission to charge a patient.
 *
 * Sharp edge inherited from `getPrimaryProviderRole`, which returns the *first*
 * code in `PRIMARY_ROLES`: a practitioner credentialed as both PHYSICIAN and
 * PATIENT_ACCESS is refused when PHYSICIAN is listed first. One primary role is the
 * intended data model and every other permission reads it the same way, so this
 * is not special-cased here — but if a real user is ever denied payments access
 * they should not be, check the code ORDER on their `PractitionerRole` first.
 */
export function canAccessPayments(practitionerRole: PractitionerRole | undefined): boolean {
  const role = getPrimaryProviderRole(practitionerRole);

  return (!!role && PAYMENTS_ROLES.includes(role)) || isAdmin(practitionerRole);
}

/**
 * Whether the user may see payment orders, ledgers and billing rows. True for
 * everyone `canAccessPayments` admits, plus Finance, who reads billing but
 * does not create orders, send links or record D2C payments (BAR-2648). The
 * `create-treatment-payment` bot applies this to its read operations and
 * `canAccessPayments` to its writes, so the two predicates are the whole gate.
 */
export function canViewPayments(practitionerRole: PractitionerRole | undefined): boolean {
  const role = getPrimaryProviderRole(practitionerRole);

  return (!!role && PAYMENTS_VIEW_ROLES.includes(role)) || isAdmin(practitionerRole);
}

/**
 * Whether the user may record employer benefit events (BAR-2593). The server boundary is
 * BenefitEventWriteAccessPolicy for BENEFIT_EVENT_WRITE_ROLES and AdminAccessPolicy for
 * administrators; this decides whether Orbit offers the action. Fails closed with no role.
 */
export function canRecordBenefitEvents(practitionerRole: PractitionerRole | undefined): boolean {
  const role = getPrimaryProviderRole(practitionerRole);
  return (!!role && BENEFIT_EVENT_WRITE_ROLES.includes(role)) || isAdmin(practitionerRole);
}

/**
 * Whether the user may edit a patient's insurance card: insurer, member ID, group, subscriber
 * and coverage source (BAR-2917). The authorization boundary in the `update-insurance-coverage`
 * bot. Fails closed with no role; `ProjectMembership.admin` is not part of it.
 */
export function canEditInsuranceCard(practitionerRole: PractitionerRole | undefined): boolean {
  const role = getPrimaryProviderRole(practitionerRole);
  return (!!role && INSURANCE_CARD_EDIT_ROLES.includes(role)) || isAdmin(practitionerRole);
}

/**
 * Whether the user sees the Intake hub on /Patient (BAR-2788). Same shape as
 * `canViewPayments`: the role list plus administrators, failing closed on no
 * resolvable role. `ProjectMembership.admin` is deliberately not part of it.
 */
export function canViewIntakeHub(practitionerRole: PractitionerRole | undefined): boolean {
  const role = getPrimaryProviderRole(practitionerRole);

  return (!!role && INTAKE_HUB_ROLES.includes(role)) || isAdmin(practitionerRole);
}

/** Whether the user sees the Coverage pill (BAR-3093): care ops and administrators. */
export function canViewCoverageHub(practitionerRole: PractitionerRole | undefined): boolean {
  const role = getPrimaryProviderRole(practitionerRole);

  return (!!role && COVERAGE_HUB_ROLES.includes(role)) || isAdmin(practitionerRole);
}

/** Whether the user sees the Finance ops page (BAR-3024): Finance and administrators. */
export function canViewFinanceOps(practitionerRole: PractitionerRole | undefined): boolean {
  return getPrimaryProviderRole(practitionerRole) === ProviderRole.FINANCE || isAdmin(practitionerRole);
}

/**
 * Whether the user may read patient conversations — the Communication record
 * behind the patient timeline. False for CONVERSATIONS_HIDDEN_ROLES (Finance,
 * BAR-2648). Fails closed on no resolvable role, like its neighbours.
 */
export function canViewConversations(practitionerRole: PractitionerRole | undefined): boolean {
  const role = getPrimaryProviderRole(practitionerRole);
  return isAdmin(practitionerRole) || (!!role && !CONVERSATIONS_HIDDEN_ROLES.includes(role));
}

/**
 * Whether the user may see the clinical record — encounter notes, prescriptions,
 * orders and results. Fails closed like its neighbours: no resolvable primary
 * role means no chart. Admins always may. False for the relationship roles in
 * CLINICAL_RECORD_HIDDEN_ROLES (Patient Access, BAR-2585); the access policy is
 * the boundary, this only keeps Orbit from drawing what the policy refuses.
 */
export function canViewClinicalRecord(practitionerRole: PractitionerRole | undefined): boolean {
  const role = getPrimaryProviderRole(practitionerRole);
  return isAdmin(practitionerRole) || (!!role && !CLINICAL_RECORD_HIDDEN_ROLES.includes(role));
}

/**
 * Whether the user may edit the on-call schedule.
 *
 * Viewing is not gated at all — CompartmentAccessPolicy grants every provider readonly
 * Schedule and Slot, so the page renders for the whole company and this predicate only
 * decides whether its controls are live. Administrators are covered by AdminAccessPolicy;
 * the other roles are granted OnCallScheduleAccessPolicy by practitionerrole-subscription,
 * so this matches what the server actually permits.
 */
export function canEditOnCallSchedule(practitionerRole: PractitionerRole | undefined): boolean {
  if (!practitionerRole) return false;

  if (isAdmin(practitionerRole)) {
    return true;
  }

  const roleCode = getPrimaryProviderRole(practitionerRole);
  return !!roleCode && ON_CALL_SCHEDULE_EDIT_ROLES.includes(roleCode);
}

/**
 * Whether the user may start and save a Patient Access call card. Everyone but the
 * read-only roles (auditor, research fellow, marketing) — the three base policies grant
 * the write on category alone, so a role that resolves to nothing (an admin, a new account)
 * is not refused.
 */
export function canCreatePatientAccessNote(practitionerRole: PractitionerRole | undefined): boolean {
  const role = getPrimaryProviderRole(practitionerRole);
  return (
    role !== ProviderRole.EMERSON_QUALITY_AUDITOR &&
    role !== ProviderRole.RESEARCH_FELLOW &&
    role !== ProviderRole.MARKETING
  );
}

function getProviderRoleCode(role?: PractitionerRole): ProviderRole | undefined {
  const primary = getPrimaryProviderRole(role);
  if (primary) {
    return primary;
  }

  return isAdmin(role) ? ProviderRole.ADMINISTRATOR : undefined;
}

export function mapPractitionersToWithRole(
  practitioners: Practitioner[],
  practitionerRoles: PractitionerRole[]
): PractitionerWithRole[] {
  if (!practitioners.length) return [];

  return practitioners.map((p) => {
    const role = practitionerRoles.find(
      (r) => r.practitioner?.reference && r.practitioner.reference === getReferenceString(p)
    );

    const roleCode = getProviderRoleCode(role);
    // Label from the code, never from the display string stored on the
    // resource: a PractitionerRole provisioned before a rename still carries
    // the old name (BAR-2571), and the code is the identity.
    const roleDisplay = roleCode ? PROVIDER_ROLE_CODING_MAP[roleCode]?.display : undefined;

    return {
      ...p,
      roleLabel: roleDisplay,
      roleCode,
    };
  });
}

export function hasCustomPermission(practitionerRole: PractitionerRole, permissionUrl: string): boolean {
  const permissions = practitionerRole.extension?.find((extension) => extension.url === CUSTOM_PERMISSIONS_EXTENSION);
  return permissions?.extension?.find((extension) => extension.url === permissionUrl)?.valueBoolean ?? false;
}

export function checkIsPhysicianOnly(practitionerRole: PractitionerRole | undefined): boolean {
  if (!practitionerRole) return false;

  const allRoleCodes =
    practitionerRole?.code?.flatMap((code) => code.coding?.map((coding) => coding.code) ?? []).filter(Boolean) ?? [];

  return allRoleCodes.length === 1 && allRoleCodes[0] === ProviderRole.PHYSICIAN;
}

type AuthMeInfo = {
  user: User;
  membership: ProjectMembership;
  project: Project;
  profile: Practitioner;
  config: UserConfiguration;
  accessPolicy: AccessPolicy;
  security: {
    mfaEnrolled: boolean;
    sessions: Record<string, string>;
  };
};

export function getAuthMeInfo(medplum: MedplumClient): Promise<AuthMeInfo> {
  return medplum.get('auth/me');
}

export function getEmail(profile?: ProfileResource): string | undefined {
  if (!profile) return undefined;
  return profile.telecom?.find((t) => t.system === 'email')?.value;
}

export function getPractitionerWorkPhoneContactIndex(practitioner: Practitioner): number {
  const workPhoneWithValueIndex =
    practitioner.telecom?.findIndex((t) => t.system === 'phone' && t.use === 'work' && t.value?.trim()) ?? -1;

  if (workPhoneWithValueIndex >= 0) {
    return workPhoneWithValueIndex;
  }

  const firstValuedPhoneIndex = practitioner.telecom?.findIndex((t) => t.system === 'phone' && t.value?.trim()) ?? -1;
  if (firstValuedPhoneIndex >= 0) {
    return firstValuedPhoneIndex;
  }

  return practitioner.telecom?.findIndex((t) => t.system === 'phone' && t.use === 'work') ?? -1;
}

export function getPractitionerWorkPhoneContact(practitioner: Practitioner): ContactPoint | undefined {
  const phoneIndex = getPractitionerWorkPhoneContactIndex(practitioner);
  if (phoneIndex < 0) {
    return undefined;
  }

  return practitioner.telecom?.[phoneIndex];
}
